import { useEffect, useRef, useState } from "react";
import "./DeleteModal.css";
import { Box } from "@mui/material";
import Title from "../title/Title";
import {
  PrimaryButton,
  SecondaryButton,
} from "../buttonComponent/ButtonComponent";
import ModalCloseButton from "./ModalCloseButton";
import InviteUsers from "../../image/usersPlus.svg";
import TextInput from "../textInput/TextInput";
import Dropdown from "../dropdown/Dropdown";
import Checkbox from "../checkbox/Checkbox";
import { APIInviteAdministrators } from "../../config/utilities/APIs";
import { useAuth } from "../../config/utilities/useAuth";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  width: "min(60rem, calc(100vw - 2rem))",
  overflow: "hidden",
  maxHeight: "92vh",
};

export default function InviteUsersModal({
  isOpen,
  onClose,
  setShowSnackbar,
  setSnackbarSubtitle,
  setSnackbarTitle,
  setSnackbarType,
  loading,
  setLoading,
  handleReset,
  fetchAdministrators,
  institutionIdOverride,
}) {
  const { privilege, token } = useAuth();
  const role = privilege?.role;
  const institutionId = institutionIdOverride ?? privilege?.institutionId;

  const accessPrivilegeOptions = [
    { label: "Viewer", value: "VIEWER" },
    { label: "Editor", value: "EDITOR" },
    { label: "Admin", value: "ADMIN" },
    ...(role === "SUPERADMIN"
      ? [{ label: "Superadmin", value: "SUPERADMIN" }]
      : []),
  ];

  const privilegesWithCheckboxes = [
    {
      name: "Live Module",
      checkboxes: [
        { key: "mapOverview", label: "Map Overview" },
        { key: "dispatchBoard", label: "Dispatch Board" },
      ],
    },
    {
      name: "Fleet Management Module",
      checkboxes: [
        { key: "vehicles", label: "Vehicles" },
        { key: "drivers", label: "Drivers" },
      ],
    },
    {
      name: "Manage Users Module",
      checkboxes: [
        { key: "administrators", label: "Administrators" },
        { key: "familyManagement", label: "Family Management" },
      ],
    },
    {
      name: "Schedule Management Module",
      checkboxes: [{ key: "routeManagement", label: "Route Management" }],
    },
    {
      name: "Notifications Campaign Module",
      checkboxes: [{ key: "campaignManagement", label: "Campaign Management" }],
    },
    {
      name: "Scheduling Module",
      checkboxes: [{ key: "scheduling", label: "Scheduling" }],
    },
  ];

  const initializePrivileges = () => {
    const privilegesObj = {};
    privilegesWithCheckboxes.forEach((module) => {
      module.checkboxes.forEach((checkbox) => {
        privilegesObj[checkbox.key] = false;
      });
    });
    return privilegesObj;
  };

  const [input, setInput] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    privileges: initializePrivileges(),
  });

  const [error, setError] = useState({
    firstName: false,
    lastName: false,
    email: false,
    role: false,
    privileges: false,
  });

  const [keyUp, setKeyUp] = useState("", false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvite = async () => {
    setLoading(true);

    // Base admin data
    let adminData = {
      institutionId: institutionId,
      firstName: input.firstName,
      lastName: input.lastName,
      email: input.email,
      role: input.role,
    };

    // Only include privileges if the role is "editor" or "viewer"
    if (["EDITOR", "VIEWER"].includes(input.role)) {
      adminData.privileges = Object.fromEntries(
        Object.entries(input.privileges).map(([key, value]) => [
          key,
          value ? "TRUE" : "FALSE",
        ])
      );
    }

    try {
      const response = await APIInviteAdministrators(adminData, setLoading, institutionId, token);

      if (response.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Invite Successful");
        setSnackbarSubtitle(
          "Invitation to the workspace has been sent via email"
        );
        setSnackbarType("success");
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to invite admin"
        );
      }
    } catch (error) {
      let errorMessage = error.message || "An error occurred";

      if (
        errorMessage === "Bad request: Admin user already exists in MongoDB."
      ) {
        errorMessage = "This admin already exists in the workspace.";
      }

      setShowSnackbar(true);
      setSnackbarTitle("Invite Failed");
      setSnackbarSubtitle(errorMessage);
      setSnackbarType("error");
    } finally {
      await fetchAdministrators();
      setLoading(false);
      handleResetInputs();
      handleReset();
    }
  };

  const handleDropdownChange = (e) => {
    setInput((prev) => ({ ...prev, role: e.target.value }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setInput((prev) => ({
      ...prev,
      privileges: {
        ...prev.privileges,
        [value]: checked,
      },
    }));
  };

  const isFormValid = () => {
    let isValid = true;

    // Regular expressions
    const nameRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+$/;

    // Convert privilege object to an array of selected (true) values
    const selectedPrivileges = Object.values(input.privileges || {}).some(
      (value) => value === true
    );

    // Validate fields
    const newErrors = {
      firstName:
        input.firstName.trim() !== "" && !nameRegex.test(input.firstName),
      lastName: input.lastName.trim() !== "" && !nameRegex.test(input.lastName),
      email: input.email.trim() !== "" && !emailRegex.test(input.email),
      role: input.role.trim() === "",
      privileges:
        (input.role === "EDITOR" || input.role === "VIEWER") &&
        !selectedPrivileges,
    };

    // Determine overall validity
    isValid =
      !Object.values(newErrors).includes(true) &&
      input.firstName.trim() !== "" &&
      input.lastName.trim() !== "" &&
      input.email.trim() !== "";

    // Set errors in state
    setError(newErrors);

    return isValid;
  };

  useEffect(() => {
    setIsSubmitDisabled(!isFormValid());
  }, [input]);

  const handleResetInputs = () => {
    setInput({
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      privileges: initializePrivileges(),
    });

    setError({
      firstName: false,
      lastName: false,
      email: false,
      role: false,
      privileges: false,
    });

    setKeyUp("");
  };

  const handleClose = () => {
    handleResetInputs();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <Box sx={style} className="profile-modal-box invite-users-modal">
        <div className="user-modal-header">
          <div className="user-modal-header-contents">
            <div className="icon-button-container">
              <div className="icon-container">
                <img
                  style={{ margin: "auto" }}
                  src={InviteUsers}
                  alt="invite-users-icon"
                  loading="lazy"
                />
              </div>
              <div className="x-button-container">
                <ModalCloseButton
                  onClick={handleClose}
                  className="user-x-button"
                />
              </div>
            </div>
            <div className="user-modal-header-title">
              <Title
                title="Invite users"
                subtitle="Invite a new administrator to the BusBuddy admin platform"
                titleSize="1.125rem"
                subtitleSize="0.875rem"
                gap="0.25rem"
                lineHeight="1.75rem"
              />
            </div>
          </div>
        </div>
        <div
          ref={contentRef}
          className={`user-modal-content ${
            isOverflowing ? "has-overflow" : ""
          }`}
        >
          <div className="name-container">
            <div className="user-modal-text">Name</div>
            <div className="invite-name-container">
              <div className="input-container">
                <TextInput
                  label="First Name"
                  placeholder="Enter first name"
                  setInput={setInput}
                  input={input}
                  name="firstName"
                  type="text"
                  error={error.firstName}
                  setError={(value) => setError({ ...error, firstName: value })}
                  errorMessage={"Enter Valid Name"}
                  onChangeFunction={handleInputChange}
                  width="19.5rem"
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                />
              </div>
              <div>
                <TextInput
                  label="Last Name"
                  placeholder="Enter last name"
                  setInput={setInput}
                  input={input}
                  name="lastName"
                  type="text"
                  error={error.lastName}
                  setError={(value) => setError({ ...error, lastName: value })}
                  errorMessage={"Enter Valid Name"}
                  onChangeFunction={handleInputChange}
                  width="19.5rem"
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                />
              </div>
            </div>
          </div>
          <div className="email-container">
            <div className="user-modal-text">Email Address</div>
            <div className="invite-text-input-container">
              <TextInput
                width="41.9rem"
                placeholder="Enter email"
                setInput={setInput}
                input={input}
                name="email"
                type="text"
                error={error.email}
                setError={(value) => setError({ ...error, email: value })}
                errorMessage={"Enter Valid Email"}
                onChangeFunction={handleInputChange}
                keyUp={keyUp}
                setKeyUp={setKeyUp}
              />
            </div>
          </div>
          <div className="access-privilege-container">
            <div className="user-modal-text">Access Privilege</div>
            <div className="invite-text-input-container">
              <Dropdown
                placeholder="Choose access privilege"
                options={accessPrivilegeOptions}
                name="role"
                error={false}
                onChangeFunction={handleDropdownChange}
                input={{ role: input.role }}
                width="43.8rem"
              />
            </div>
          </div>
          {["EDITOR", "VIEWER"].includes(input.role) && (
            <div className="selected-modules-container">
              <div className="user-modal-text">Selected Modules</div>
              <div className="modules-grid">
                {privilegesWithCheckboxes.map((module, index) => (
                  <div className="module-item" key={index}>
                    <div className="module-title">{module.name}</div>
                    <div className="checkbox-group">
                      {module.checkboxes.map((checkbox, checkboxIndex) => (
                        <Checkbox
                          key={checkboxIndex}
                          value={checkbox.key} // Use key
                          text={checkbox.label} // Use label
                          width="16px"
                          height="16px"
                          onClick={handleCheckboxChange}
                          checked={input.privileges[checkbox.key] || false} // Default to false
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="button-container-delete">
          <SecondaryButton
            width="14rem"
            label="Cancel"
            imageIcon={false}
            onClickFunction={handleClose}
          />
          <PrimaryButton
            width="14rem"
            label="Invite User"
            onClickFunction={handleInvite}
            disable={isSubmitDisabled}
            loading={loading}
          />
        </div>
      </Box>
    </div>
  );
}
